import { isDefined } from "@clipboard-health/util-ts";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useCallback, useEffect, useMemo } from "react";

export const DISMISSED_EXTRA_WORKED_TIME_REQUESTS_STORAGE_KEY = "dismissedExtraWorkedTimeRequests";

/**
 * Hook to manage dismissed extra worked time request IDs in localStorage.
 *
 * @param currentRequestIds - Array of request IDs currently returned by the API,
 *                            or undefined if data hasn't loaded yet.
 *                            Used to clean up stale dismissed IDs from localStorage.
 */
export function useDismissedExtraWorkedTimeRequests(currentRequestIds?: string[]) {
  const [dismissedRequestIds, setDismissedRequestIds] = useLocalStorage<string[]>(
    DISMISSED_EXTRA_WORKED_TIME_REQUESTS_STORAGE_KEY,
    []
  );

  // Clean up dismissed IDs that are no longer in the API response
  useEffect(() => {
    // Skip cleanup if data hasn't loaded yet
    if (!isDefined(currentRequestIds)) {
      return;
    }

    const dismissedIdsToPreserve = dismissedRequestIds.filter((dismissedId) =>
      currentRequestIds.includes(dismissedId)
    );

    if (dismissedIdsToPreserve.length !== dismissedRequestIds.length) {
      setDismissedRequestIds(dismissedIdsToPreserve);
    }
  }, [currentRequestIds, dismissedRequestIds, setDismissedRequestIds]);

  const isRequestDismissed = useCallback(
    (requestId: string): boolean => dismissedRequestIds.includes(requestId),
    [dismissedRequestIds]
  );

  const dismissRequest = useCallback(
    (requestId: string) => {
      setDismissedRequestIds((previous) => [...previous, requestId]);
    },
    [setDismissedRequestIds]
  );

  return useMemo(
    () => ({
      isRequestDismissed,
      dismissRequest,
    }),
    [isRequestDismissed, dismissRequest]
  );
}
